import { FC, ReactNode, memo } from "react";

import { Theme } from "apps/website/maps/Theme.map";
import cx from "apps/website/utils/misc/cx";

import {
  paddingClassMap,
  Padding,
  Size,
  sizeClassMap,
} from "./Container.map";

export interface IContainer {
  /** Horizontal padding applied to the container */
  padding?: Padding;
  /** Additional CSS classes applied to the container */
  className?: string;
  /** Theme applied to the container for styling */
  theme?: Theme;
  /** Component identifier for debugging and testing */
  component?: string;
  /** Maximum width constraint for the container */
  size?: Size;
  /** Content to be rendered inside the container */
  children: ReactNode;
  /** Test ID for automated testing */
  dataTestId?: string;
}

/**
 * Container component
 *
 * A layout primitive that constrains content to a max width and applies horizontal padding.
 * Often used inside a <Section> but can be used anywhere consistent alignment is needed.
 *
 * @param padding Horizontal padding (e.g. 'default', 'large', or 'none')
 * @param size Max-width constraint (e.g. 'default', 'sm', 'md')
 * @param theme Optional theme context via data-theme
 * @param className Optional custom class overrides
 * @param component Debug label for devtools
 * @param dataTestId Used for test selectors
 * @param children Content to be rendered inside the container
 *
 * @example
 * ```tsx
 * <Container padding="default" size="default">
 *   <h1>Page Title</h1>
 *   <p>Page content goes here...</p>
 * </Container>
 * ```
 *
 * @example
 * ```tsx
 * <Container padding="large" size="sm" theme="brand">
 *   <div>Hero section content</div>
 * </Container>
 * ```
 */

const Container: FC<IContainer> = ({ padding = "default", className, theme, component = "Container", size = "default", children, dataTestId }) => (
  <div
    data-component={component}
    className={cx(`mx-auto ${sizeClassMap[size]} ${paddingClassMap[padding]} ${className}`)}
    data-theme={theme}
    data-testid={dataTestId}
  >
    { children }
  </div>
);

export default memo(Container);
